// useChatStream.ts
import { useCallback, useRef, useState } from 'react';
import { chatStream, ChatSSEvent } from '@lib/sse-chat';
import { getAccessToken } from '@services/authentication';
import * as Sentry from '@sentry/react';

export function useChatStream(url: string) {
  const [response, setResponse] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const controllerRef = useRef<AbortController | null>(null);

  const send = useCallback(
    async (tripProfileId: string, message: string) => {
      if (isStreaming) return;
      setIsStreaming(true);
      const controller = new AbortController();
      controllerRef.current = controller;

      try {
        const obtainToken = async () => (await getAccessToken()) ?? undefined;

        const attempt = async () => {
          const token = await obtainToken();
          return chatStream({
            url,
            body: { tripProfileId, message },
            accessToken: token,
            signal: controller.signal,
            onEvent: (event: ChatSSEvent, payload: unknown) => {
              if (event === 'status' || event === 'keep-alive' || event === 'keepalive') {
                // optional: surface status updates to UI; keep aggregated text unchanged
                return;
              }
              if (event === 'itinerary') {
                // Replace received text with the serialized itinerary object for downstream parsing
                try {
                  setResponse(typeof payload === 'string' ? payload : JSON.stringify(payload));
                } catch {
                  // ignore
                }
                return;
              }
              if (event === 'finish') {
                console.log('SSE finish:', payload);
                setResponse('');
                return;
              }
              if (event === 'error') {
                Sentry.captureException(Error('useChatStream: SSE Error'), { extra: { payload } });
                console.error('SSE error:', payload);
                return;
              }
            },
          });
        };

        let finalText: string | undefined;
        try {
          finalText = await attempt();
        } catch (error: unknown) {
          console.error('SSE ERROR:', error);
          const messageText =
            typeof error === 'object' && error !== null && 'message' in error
              ? String((error as { message?: unknown }).message)
              : '';
          const isAuthError = /401|403|Unauthorized|Forbidden/i.test(messageText);
          if (!controller.signal.aborted && isAuthError) {
            // Try once more with a freshly obtained token
            finalText = await attempt();
          } else {
            throw error;
          }
        }

        setResponse(finalText ?? '');
      } finally {
        console.log('useChatStream finial setString False');
        try {
          controller.abort();
        } catch {
          // ignore
        }
        setIsStreaming(false);
        controllerRef.current = null;
      }
    },
    [url, isStreaming]
  );

  const abort = useCallback(() => {
    controllerRef.current?.abort();
  }, []);

  return { isStreaming, response, send, abort };
}
